/**
 * services/MarketSnapshotSettingsService.ts
 * Market snapshot settings management service
 *
 * Provides singleton settings for market snapshot field configuration.
 * Users define which fields to capture when recording market state observations.
 */

import { BaseService } from './BaseService';
import type {
  MarketSnapshotSettings,
  SnapshotFieldDefinition,
} from '@/types/schema/MarketSnapshotSettings';

/**
 * Service for managing market snapshot settings
 *
 * Extends BaseService to provide standard CRUD operations plus
 * specialized methods for getting or creating default settings.
 */
export class MarketSnapshotSettingsService extends BaseService<MarketSnapshotSettings> {
  constructor() {
    super('market_snapshot_settings');
  }

  /**
   * Get default market snapshot settings
   *
   * @returns Default settings object (without id, timestamps, or sync fields)
   * @private
   */
  private getDefaultSettings(): Omit<
    MarketSnapshotSettings,
    'id' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'deletedByDevice'
  > {
    return {
      // Empty array - user configures their own snapshot fields
      snapshotFields: [],
      // Empty array - user configures their preferred instrument/timeframe defaults
      defaultSnapshots: [],
    };
  }

  /**
   * Get existing settings or create default settings if none exist
   *
   * This is the primary method for retrieving market snapshot settings.
   * On first use, it creates an empty settings record that the user
   * can then customize with their preferred snapshot fields.
   *
   * @returns Promise resolving to MarketSnapshotSettings
   */
  async getOrCreateDefaultSettings(): Promise<MarketSnapshotSettings> {
    try {
      // Check if settings already exist (getAll excludes tombstones)
      const allSettings = await this.getAll();

      if (allSettings.length > 0) {
        // Return first settings record (singleton semantics)
        const firstSetting = allSettings[0];
        if (!firstSetting) {
          throw new Error('Unexpected undefined settings record');
        }
        // Normalize defaultSnapshots to [] if undefined (defensive)
        return {
          ...firstSetting,
          defaultSnapshots: firstSetting.defaultSnapshots ?? [],
        };
      }

      // No settings exist, create default
      const defaultSettings = this.getDefaultSettings();
      const createdSettings = await this.add(defaultSettings);

      return createdSettings;
    } catch (error) {
      console.error('Failed to get or create market snapshot settings:', error);
      throw new Error('Failed to initialize market snapshot settings');
    }
  }

  /**
   * Get current market snapshot settings
   *
   * Alias for getOrCreateDefaultSettings() for semantic clarity.
   *
   * @returns Promise resolving to current MarketSnapshotSettings
   */
  async getCurrentSettings(): Promise<MarketSnapshotSettings> {
    return this.getOrCreateDefaultSettings();
  }

  /**
   * Update the snapshot field definitions
   *
   * Validates all fields before persisting. Deduplicates by field ID,
   * keeping the first occurrence of each ID.
   *
   * @param fields - Array of SnapshotFieldDefinition objects (empty array allowed)
   * @returns Promise resolving to updated MarketSnapshotSettings
   * @throws Error if any field has empty/missing id
   * @throws Error if any field has empty name
   * @throws Error if dropdown field has no options or empty options array
   * @throws Error if dropdown field has any option with empty id or label
   * @throws Error if dropdown field has duplicate option IDs
   */
  async updateSnapshotFields(
    fields: SnapshotFieldDefinition[]
  ): Promise<MarketSnapshotSettings> {
    // Validate ALL fields before deduplication
    for (const field of fields) {
      // Field-level validation
      if (!field.id || field.id.trim() === '') {
        throw new Error('Snapshot field validation failed: field id is required');
      }
      if (!field.name || field.name.trim() === '') {
        throw new Error(
          `Snapshot field validation failed: field "${field.id}" has empty name`
        );
      }

      // Dropdown-specific validation
      if (field.type === 'dropdown') {
        if (!field.options || field.options.length === 0) {
          throw new Error(
            `Snapshot field validation failed: dropdown field "${field.name}" must have at least one option`
          );
        }

        // Validate each option
        const optionIds = new Set<string>();
        for (const option of field.options) {
          if (!option.id || option.id.trim() === '') {
            throw new Error(
              `Snapshot field validation failed: dropdown field "${field.name}" has option with empty id`
            );
          }
          if (!option.label || option.label.trim() === '') {
            throw new Error(
              `Snapshot field validation failed: dropdown field "${field.name}" has option with empty label`
            );
          }
          if (optionIds.has(option.id)) {
            throw new Error(
              `Snapshot field validation failed: dropdown field "${field.name}" has duplicate option id "${option.id}"`
            );
          }
          optionIds.add(option.id);
        }
      }
    }

    // Deduplicate by field ID (first occurrence wins)
    const seenIds = new Set<string>();
    const uniqueFields = fields.filter((field) => {
      if (seenIds.has(field.id)) {
        return false;
      }
      seenIds.add(field.id);
      return true;
    });

    // Ensure settings record exists
    const currentSettings = await this.getOrCreateDefaultSettings();

    // Persist updated fields
    const updatedSettings = await this.update(currentSettings.id, {
      snapshotFields: uniqueFields,
    });

    return updatedSettings;
  }

  /**
   * Update the default snapshot instrument/timeframe pairs
   *
   * Validates all entries before persisting. Rejects duplicates (UI validates first,
   * so duplicates indicate a bug). Preserves exact array order as this drives
   * auto-population order in T05.
   *
   * @param defaultSnapshots - Array of instrument/timeframe pairs (empty array allowed)
   * @returns Promise resolving to updated MarketSnapshotSettings
   * @throws Error if any entry has empty/missing instrumentId
   * @throws Error if any entry has empty/missing timeframe
   * @throws Error if duplicate instrumentId+timeframe combination found
   */
  async updateDefaultSnapshots(
    defaultSnapshots: Array<{ instrumentId: string; timeframe: string }>
  ): Promise<MarketSnapshotSettings> {
    // Track seen combinations for duplicate detection
    const seenCombinations = new Set<string>();

    // Validate all entries
    for (const entry of defaultSnapshots) {
      // Validate instrumentId
      if (!entry.instrumentId || entry.instrumentId.trim() === '') {
        throw new Error(
          'Default snapshot validation failed: instrumentId is required'
        );
      }

      // Validate timeframe
      if (!entry.timeframe || entry.timeframe.trim() === '') {
        throw new Error(
          'Default snapshot validation failed: timeframe is required'
        );
      }

      // Check for duplicate combination
      const key = `${entry.instrumentId}:${entry.timeframe}`;
      if (seenCombinations.has(key)) {
        throw new Error(
          `Default snapshot validation failed: duplicate instrumentId+timeframe combination "${entry.instrumentId}" + "${entry.timeframe}"`
        );
      }
      seenCombinations.add(key);
    }

    // Ensure settings record exists
    const currentSettings = await this.getOrCreateDefaultSettings();

    // Persist updated defaults (preserving exact array order)
    const updatedSettings = await this.update(currentSettings.id, {
      defaultSnapshots,
    });

    return updatedSettings;
  }
}

// Export singleton instance for use throughout the application
export const marketSnapshotSettingsService = new MarketSnapshotSettingsService();
